import moment from 'moment';
import 'moment/locale/fr-ca';
import 'moment/locale/en-ca';

export interface FormatOptions {
  addRelative?: boolean;
}

type SupportedFormatKey = 'en-US' | 'en-CA' | 'fr-CA';

const DATE_FORMATS: Record<SupportedFormatKey, string> = {
  'en-US': 'MMM D, YYYY', // Jun 30, 2025
  'en-CA': 'YYYY-MM-DD', // 2025-06-30
  'fr-CA': 'D MMM YYYY', // 30 janv. 2025
};

const TIME_FORMATS: Record<SupportedFormatKey, string> = {
  'en-US': 'h:mm A', // 2:41 PM
  'en-CA': 'HH:mm', // 14:41
  'fr-CA': 'HH [h] mm', // 14 h 41
};

const resolveLocale = (fullLocale: string): SupportedFormatKey => {
  if (fullLocale === 'fr-US') return 'fr-CA';
  if (fullLocale === 'en-CA' || fullLocale === 'fr-CA') return fullLocale;
  return 'en-US';
};

function getMomentInstance(date: Date | string | number, locale: string) {
  return moment.utc(date).local().locale(locale.toLowerCase());
}

function getRelativePart(date: any, locale: string): string {
  const targetLocale = resolveLocale(locale);
  const relativePart = getMomentInstance(date, targetLocale).fromNow();
  return `(${relativePart})`;
}

export function formatDate(date: any, locale: string, options?: FormatOptions) {
  if (!date) return '';

  const targetLocale = resolveLocale(locale);
  const formattedDate = getMomentInstance(date, targetLocale).format(
    DATE_FORMATS[targetLocale],
  );

  return options?.addRelative
    ? `${formattedDate} ${getRelativePart(date, locale)}`
    : formattedDate;
}

export function formatTime(date: any, locale: string, options?: FormatOptions) {
  if (!date) return '';

  const targetLocale = resolveLocale(locale);
  const formattedTime = getMomentInstance(date, targetLocale).format(
    TIME_FORMATS[targetLocale],
  );

  return options?.addRelative
    ? `${formattedTime} ${getRelativePart(date, locale)}`
    : formattedTime;
}

export function formatDateTime(
  date: any,
  locale: string,
  options?: FormatOptions,
) {
  if (!date) return '';
  const targetLocale = resolveLocale(locale);
  const formattedDateTime = getMomentInstance(date, targetLocale).format(
    `${DATE_FORMATS[targetLocale]}, ${TIME_FORMATS[targetLocale]}`,
  );

  return options?.addRelative
    ? `${formattedDateTime} ${getRelativePart(date, locale)}`
    : formattedDateTime;
}

/**
 * en-US/en-CA: 1,234.56
 * fr-CA/fr-US: 1 234,56
 */
export function formatNumber(
  value: number | string | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
) {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (num === null || num === undefined || isNaN(num)) {
    return '';
  }

  const targetLocale = resolveLocale(locale);

  return new Intl.NumberFormat(targetLocale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(num);
}
